import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import supabase from '../../supabase';
import './AddProductPopup.css'

const AddProductPopup = ({ 
  formData, 
  setFormData, 
  onSave, 
  onCancel, 
  isEditing = false,
  title = 'Add New Product',
  saveButtonText = 'Add Product',
  loading = false,
  onRefresh // Add this prop to refresh products after bulk upload
}) => {
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);

  // Prevent background scrolling when popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }

    setCsvUploading(true);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const user = await supabase.auth.getUser();
          if (!user.data.user) {
            alert('User not authenticated');
            setCsvUploading(false);
            return;
          }

          const validProducts = results.data
            .filter(
              (row) =>
                row.product_name &&
                row.price_selling &&
                !isNaN(parseFloat(row.price_selling))
            )
            .map((row) => ({
              product_name: row.product_name.trim(),
              description: row.description?.trim() || '',
              price_selling: parseFloat(row.price_selling),
              is_active: true,
              user_id: user.data.user.id,
            }));

          if (validProducts.length === 0) {
            alert('No valid products found in CSV');
            setCsvUploading(false);
            return;
          }

          const { error } = await supabase.from("products").insert(validProducts);
          if (error) {
            console.error(error);
            alert('Upload failed: ' + error.message);
          } else {
            alert(`${validProducts.length} products uploaded successfully!`);
            setCsvFile(null);
            setShowCSVUpload(false);
            if (onRefresh) onRefresh(); // Refresh the products list
            onCancel(); // Close the main popup
          }
        } catch (err) {
          console.error('Unexpected error during upload:', err);
          alert('An unexpected error occurred during upload');
        } finally {
          setCsvUploading(false);
        }
      },
      error: (error) => {
        console.error('Parsing error:', error);
        alert(`Parsing error: ${error.message}`);
        setCsvUploading(false);
      }
    });
  };

  const downloadSampleCSV = () => {
    const csv = Papa.unparse([
      { product_name: 'Product A', description: 'Optional description', price_selling: 10.5 },
      { product_name: 'Product B', description: '', price_selling: 5.0 },
      { product_name: 'Product C', description: 'Another product', price_selling: 15.99 },
    ]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_products.csv");
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUploadClick = () => {
    setShowCSVUpload(true);
  };

  return (
    <div className="ProductPopup__popup-overlay">
      <div className="ProductPopup__popup">
        <div className="ProductPopup__popup-header">
          <h3 className="ProductPopup__popup-title">{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            className="ProductPopup__popup-close"
          >
            ×
          </button>
        </div>
        
        <div className="ProductPopup__popup-content">
          <div className="ProductPopup__field-group">
            <label className="ProductPopup__label">Product Name *</label>
            <input 
              name="product_name"
              placeholder="Enter product name" 
              className="ProductPopup__input" 
              value={formData.product_name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="ProductPopup__field-group">
            <label className="ProductPopup__label">Description</label>
            <textarea 
              name="description"
              placeholder="Enter description (optional)" 
              className="ProductPopup__textarea" 
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>
          
          <div className="ProductPopup__field-group">
            <label className="ProductPopup__label">Selling Price *</label>
            <input 
              name="price_selling"
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter selling price" 
              className="ProductPopup__input" 
              value={formData.price_selling}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        <div className="ProductPopup__popup-footer">
          <button
            type="button"
            onClick={onCancel}
            className="ProductPopup__button ProductPopup__button--cancel"
            disabled={loading || csvUploading}
          >
            Cancel
          </button>
          
          {!isEditing && (
            <button
              type="button"
              onClick={handleBulkUploadClick}
              className="ProductPopup__button ProductPopup__button--bulk"
              disabled={loading || csvUploading}
            >
              Bulk Upload
            </button>
          )}
          
          <button
            type="button"
            onClick={onSave}
            className="ProductPopup__button ProductPopup__button--save"
            disabled={!formData.product_name.trim() || !formData.price_selling || loading || csvUploading}
          >
            {loading ? 'Processing...' : saveButtonText}
          </button>
        </div>
      </div>

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <div className="ProductPopup__csv-overlay">
          <div className="ProductPopup__csv-popup">
            <div className="ProductPopup__popup-header">
              <h3 className="ProductPopup__popup-title">Bulk Upload Products</h3>
              <button
                type="button"
                onClick={() => setShowCSVUpload(false)}
                className="ProductPopup__popup-close"
              >
                ×
              </button>
            </div>
            
            <div className="ProductPopup__popup-content">
              <div className="ProductPopup__field-group">
                <label className="ProductPopup__label">Select CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="ProductPopup__input"
                />
              </div>
              
              <div className="ProductPopup__csv-actions">
                <button
                  className="ProductPopup__button ProductPopup__button--secondary"
                  onClick={downloadSampleCSV}
                  disabled={csvUploading}
                >
                  Download Sample CSV
                </button>
              </div>
            </div>
            
            <div className="ProductPopup__popup-footer">
              <button
                type="button"
                onClick={() => setShowCSVUpload(false)}
                className="ProductPopup__button ProductPopup__button--cancel"
                disabled={csvUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCSVUpload}
                className="ProductPopup__button ProductPopup__button--save"
                disabled={!csvFile || csvUploading}
              >
                {csvUploading ? 'Uploading...' : 'Upload CSV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProductPopup;
